import React, { useContext, useState, FC, useEffect } from 'react';
import {Translation, Key} from './Model/TranslationOutput';
import TranslationService from './services/TranslationService';
import { UseConfigContext } from "./ConfigContext";
import { toast } from 'react-toastify';

interface ITranslationContext {
    currentLanguage: string, 
    translation: Translation,
    updateKeys?: (params:Translation) => void,
    changeLanguage?: (lang:string) => void,
    getKeyFromKeyId: (keyId:string) => string,
    isLoaded: boolean
}

const defaultTranslationContext = {
    currentLanguage: "fr",
    translation: new Translation(),
    getKeyFromKeyId: () => { return ''; },
    isLoaded: false
}

interface ITranslationProvider {
    children?: React.ReactNode,
}

const TranslationContext = React.createContext<ITranslationContext>(defaultTranslationContext);
export const UseTranslationContext = () => useContext(TranslationContext);

export const TranslationContextProvider:FC<ITranslationProvider> = ({children}) => {
    const [translation, setTranslation] = useState(defaultTranslationContext.translation);
    const [currentLanguage, setCurrentLanguage] = useState(defaultTranslationContext.currentLanguage);
    const [isLoaded, setIsLoaded] = useState(defaultTranslationContext.isLoaded);

    const updateKeys = (params:Translation) => {
        setTranslation(params);
    }

    function GetErrorMessage(lang : string)
    {
        switch(lang)
        {
            case 'fr':
                return 'Une erreur s\'est produite lors de la récupération des données sur le serveur. Veuillez recharger la page et réessayer.';
            break;
            case 'nl':
                return 'Er is een fout opgetreden tijdens het ophalen van gegevens van de server. Herlaad de pagina en probeer het opnieuw.';
            break;
            case 'en':
            default:
                return 'An error occured while retrieving data from the server. Please reload the page and try again.';
            break;
        }
    }


    const changeLanguage = (params:string) => {

        if(params != currentLanguage)
        {
            let newLang = '';

            if(params == 'fr')
            {
                newLang = 'fr';
            }
            else if(params == 'nl')
            {
                newLang = 'nl';
            }
            else
            {
                newLang = 'en';
            }

            setCurrentLanguage(newLang);
            document.documentElement.lang = newLang;

            const currentUrl = window.location.origin + window.location.pathname;
            const nextURL = currentUrl + '?lg=' + newLang;
            const nextTitle = 'CarSim';
            window.history.replaceState(null, nextTitle, nextURL);
        }
    }

    const getKeyFromKeyId = (keyId: string) => {

            var foundKey = translation.keys.find(myKey => myKey.keyId == keyId);
            if(foundKey != null)
            {
                if(currentLanguage == "fr")
                {
                    return foundKey.keyFr;
                }
                else if(currentLanguage == "nl")
                {
                    return foundKey.keyNl;
                }
                else
                {
                    return foundKey.keyX;
                }
            }
            else
            {
                return keyId;
            }
    }

    const { getTranslationUrl, isConfigLoaded } = UseConfigContext();
    useEffect(() => {
        if(isConfigLoaded == false)
        return;

        var carSimTranslationData = TranslationService(getTranslationUrl());

        const queryParams = new URLSearchParams(window.location.search);
        const language = queryParams.get('lg');
        let cleanedLanguageInput = 'fr'; // default is French

        if(language !== null) // The language is defined in the query
        {
            const regex = /[^A-Za-z0-9]/g;
            cleanedLanguageInput = language.replace(regex, "");
        }
        else // We try to use the browser locale
        {
            const userLocale = navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language;
            const regex = /[^A-Za-z0-9]/g;
            cleanedLanguageInput = userLocale.replace(regex, "");
        }

        carSimTranslationData.then(
            Result => 
            {
                if(typeof(Result) == 'string')
                {
                    toast(GetErrorMessage(cleanedLanguageInput), {type: 'error', theme: 'colored', autoClose:false, hideProgressBar:true, closeOnClick:true, position: 'top-left', className:'toastFullWidth'});
                }
                else
                {
                    updateKeys?.(Result as Translation);
                    setIsLoaded(true);
                }

                return Result;
            })
            .catch(e => 
            { 
                toast(GetErrorMessage(cleanedLanguageInput), {type: 'error', theme: 'colored', autoClose:false, hideProgressBar:true, closeOnClick:true, position: 'top-left', className:'toastFullWidth'});
                return ("error " + e);
            });

            if(cleanedLanguageInput != currentLanguage)
            {
                changeLanguage(cleanedLanguageInput);
            }

    },[isConfigLoaded]);

    return(
        <TranslationContext.Provider value={{
            translation,
            currentLanguage,
            updateKeys,
            changeLanguage,
            getKeyFromKeyId,
            isLoaded
        }}>
            {children}
        </TranslationContext.Provider>
    )
}